import React from "react";
import styled from "styled-components";
import gql from "graphql-tag";
import {useQuery} from "react-apollo";
import {Route, Switch} from "react-router";
import {Link, useLocation} from "react-router-dom";
import {connect} from "react-redux";
import compose from "lodash/flowRight";
import get from "lodash/get";
import {ErrorBoundary} from "@unibuddy/error-reporting";

import AnalyticsValue from "@unibuddy/machop/AnalyticsNew/AnalyticsValue/AnalyticsValue";
import {productSpec} from "@unibuddy/machop/AnalyticsNew/constants/productSpecification";
import RelativeRouter from "@unibuddy/machop/Navigation/components/RelativeRouter/RelativeRouter";
import withSourceTracking from "@unibuddy/machop/SourceTracking/withSourceTracking/withSourceTracking";
import {GlobalCssVariable} from "@unibuddy/machop/Styling/hocs/withCSSVariables";
import Split from "@unibuddy/machop/Experimental/Layout/components/Split/Split";
import ButtonLink from "@unibuddy/machop/Experimental/General/components/ButtonLink/ButtonLink";
import useAuth from "@unibuddy/machop/Auth/hooks/useAuth";
import useUniversityFeature from "@unibuddy/machop/Feature/useUniversityFeature";

import AuthRoutes from "../../Auth/components/AuthRoutes/AuthRoutes";
import ErrorPage from "../../Landing/containers/ErrorPage/ErrorPage";
import LoadingPage from "../../Layout/components/LoadingPage/LoadingPage";
import LogoutButton from "../../Auth/components/LogoutButton/LogoutButton";
import {Container, LandingPageHeader, Body} from "../../Layout/components/Layout/Layout";
import {EmbeddedWidgetProvider} from "../../EmbeddedWidget/EmbeddedWidget";
import PageTitle from "../../Layout/components/PageTitle/PageTitle";
import {landingPageQuery} from "../queries/landingPageQuery";

const Home = React.lazy(() => import(/* webpackChunkName: "landingHomePage" */ "../Home/Home"));

const EmbeddedWidget = React.lazy(() =>
    import(/* webpackChunkName: "EmbeddedWidget" */ "../../EmbeddedWidget/EmbeddedWidget"),
);

const mentorListQuery = gql`
    query MentorListQuery($universitySlug: String) {
        mentorList(universitySlug: $universitySlug) {
            mentors {
                id
                firstName
                profilePhoto
                lastSeen
                degree {
                    id
                    name
                }
                placeName
                country {
                    id
                    code
                    name
                }
                languages {
                    id
                    name
                }
                year
                pastQualification
                shortBio
                bio
                degreeLevel
            }
        }
    }
`;

const staffListQuery = gql`
    query StaffListQuery($universitySlug: String, $role: String) {
        buddies(universitySlug: $universitySlug, role: $role) {
            users {
                id
                firstName
                profilePhoto
                staffFields {
                    bio
                    department
                    role
                }
            }
        }
    }
`;

const Wrapper = styled.div`
    margin: 0 auto;
    max-width: 1280px;
`;

const Logo = styled.img`
    max-height: 50px;
`;

const HeaderBar = styled(LandingPageHeader)`
    height: 80px;
`;

const Analytics = ({match}) => {
    return (
        <React.Fragment>
            <AnalyticsValue
                name={productSpec.engagementPortal.properties.UNIVERSITY_SLUG}
                value={match.params.universitySlug}
            />
            <AnalyticsValue
                name={productSpec.engagementPortal.properties.PRODUCT}
                value={productSpec.engagementPortal.NAME}
            />
        </React.Fragment>
    );
};

const LandingPageRoutes = ({match, dispatch}) => {
    const baseUrl = match.url.endsWith("/") ? match.url.slice(0, -1) : match.url;
    const {isLoggedIn} = useAuth();
    const universitySlug = get(match, "params.universitySlug");
    const {data, loadingLandingPage, error} = useQuery(landingPageQuery, {
        variables: {
            universitySlug,
            isLoggedIn,
        },
    });

    const mentorsQuery = useQuery(mentorListQuery, {
        variables: {
            universitySlug: match.params.universitySlug,
        },
    });

    const staffQuery = useQuery(staffListQuery, {
        variables: {
            universitySlug: match.params.universitySlug,
            role: "staff",
        },
    });

    const dataMentor = get(mentorsQuery, "data.mentorList.mentors", []);
    const loadingMentor = get(mentorsQuery, "loading", false);
    const dataStaff = get(staffQuery, "data.buddies.users", []);
    const loadingStaff = get(staffQuery, "loading", false);
    const location = useLocation();
    const {loading, isFeatureEnabled} = useUniversityFeature({
        universitySlug,
        feature: "liveEventsLandingPage",
    });

    if (error) {
        return <ErrorPage />;
    }

    if (loadingLandingPage || !data || loading) {
        return <LoadingPage />;
    } else if (!isFeatureEnabled && !loading) return <ErrorPage />;

    const {liveEventLandingPage, university} = data;

    const AuthButton = () => {
        const isAuthRoute = location.pathname.includes("/auth/");
        if (isAuthRoute) return null;

        return isLoggedIn ? (
            <LogoutButton dispatch={dispatch} />
        ) : (
            <ButtonLink clear to={`${baseUrl}/auth/login`}>
                SIGN IN
            </ButtonLink>
        );
    };

    return (
        <ErrorBoundary boundaryName="liveEventsLandingPage">
            <Container>
                <EmbeddedWidgetProvider>
                    <PageTitle title={`${university.name || "Unibuddy"}`} />
                    <GlobalCssVariable name="--theme-primary" value={`#${university.colour}`} />
                    <Body>
                        <HeaderBar>
                            <Wrapper>
                                <Split verticalAlign="center">
                                    <Link
                                        to={`${baseUrl}`}
                                        aria-label="University logo"
                                        href={`${baseUrl}`}
                                    >
                                        <Logo src={university.logo} />
                                    </Link>
                                    <AuthButton />
                                </Split>
                            </Wrapper>
                        </HeaderBar>

                        <RelativeRouter path={match.url}>
                            <Analytics match={match} />
                            <Switch>
                                <Route
                                    path="/:universitySlug/landing/auth"
                                    render={(props) => <AuthRoutes {...props} />}
                                />
                                <Route
                                    path="/:universitySlug/landing/ambassadors"
                                    render={(routeProps) => <EmbeddedWidget {...routeProps} />}
                                    exact
                                />
                                <Route
                                    path="/:universitySlug/landing"
                                    render={() => (
                                        <Home
                                            liveEventLandingPage={liveEventLandingPage}
                                            baseUrl={baseUrl}
                                            dataMentor={dataMentor}
                                            widgetUrl={university.widgetUrl}
                                            dataStaff={dataStaff}
                                            loadingMentor={loadingMentor}
                                            loadingStaff={loadingStaff}
                                            universitySlug={universitySlug}
                                        />
                                    )}
                                />
                            </Switch>
                        </RelativeRouter>
                    </Body>
                </EmbeddedWidgetProvider>
            </Container>
        </ErrorBoundary>
    );
};

export default compose(connect(), withSourceTracking)(LandingPageRoutes);
